import {
  HubDto,
  KnownAccessControlDtoType,
  KnownDeliveryContainerDtoStatus,
  KnownHubDetailsDtoType,
  KnownHubDetailsKioskDtoControlledBy
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { Permissions } from '../../../../core/permissions/permissions.typings';
import { makeDefineActions } from '../../../../ui/components/pure/actions-button/actions-button.helpers';
import { ActionsButtonItem } from '../../../../ui/components/pure/actions-button/actions-button.types';
import { ActionDisableLocker } from '../../../../ui/components/pure/icons/ActionDisableLocker.icon';
import { ActionEnableLocker } from '../../../../ui/components/pure/icons/ActionEnableLocker.icon';
import { ActionOpen } from '../../../../ui/components/pure/icons/ActionOpen.icon';
import { Edit } from '../../../../ui/components/pure/icons/Edit.icon';
import { Reserve } from '../../../../ui/components/pure/icons/Reserve.icon';
import { Unreserve } from '../../../../ui/components/pure/icons/Unreserve.icon';
import { HubDetails } from '../../models/hub-details.model';

export enum LockerDetailsActions {
  OPEN_LOCKER = 'OPEN_LOCKER',
  OPEN_LOCKER_STATUS_CHANGE_SUCCESS = 'OPEN_LOCKER_STATUS_CHANGE_SUCCESS',
  ENABLE_DISABLE_LOCKER = 'ENABLE_DISABLE_LOCKER',
  ENABLE_DISABLE_LOCKER_SUCCESS = 'ENABLE_DISABLE_LOCKER_SUCCESS',
  UNLOCK_ROOM = 'UNLOCK_ROOM',
  EDIT_PROJECT_MODULES = 'EDIT_PROJECT_MODULES',
  EDIT_PROJECT_MODULES_SUCCESS = 'EDIT_PROJECT_MODULES_SUCCESS',
  RESERVE_CONTAINER = 'RESERVE_CONTAINER',
  RESERVE_CONTAINER_SUCCESS = 'RESERVE_CONTAINER_SUCCESS',
  UNRESERVE_CONTAINER = 'UNRESERVE_CONTAINER',
  UNRESERVE_CONTAINER_SUCCESS = 'UNRESERVE_CONTAINER_SUCCESS'
}

function makeLockerActions(
  projectID: string,
  deliveryContainerID: string,
  hubDetails: HubDetails,
  hub: HubDto
): ActionsButtonItem<LockerDetailsActions>[] {
  const section = hubDetails.sections?.find((s) => s.spaces?.some((sp) => sp.id === deliveryContainerID));
  const tower = hubDetails.towers?.find((t) => t.lockers?.some((l) => l.id === deliveryContainerID));
  const space = section?.spaces?.find((s) => s.id === deliveryContainerID);
  const locker = tower?.lockers?.find((l) => l.id === deliveryContainerID);

  const status = space?.status || locker?.status;
  const isKiosk = space?.isKiosk || locker?.isKiosk;

  const actions: ActionsButtonItem<LockerDetailsActions>[] = [
    {
      title: isKiosk ? 'Open kiosk' : space ? 'Open space' : 'Open locker',
      Icon: ActionOpen,
      action: LockerDetailsActions.OPEN_LOCKER,
      payload: {
        deliveryContainerID
      },
      permission: {
        action: Permissions.deliveryContainers.open,
        projectID
      }
    },
    {
      title:
        status !== KnownDeliveryContainerDtoStatus.Active
          ? `Enable ${space ? 'space' : 'locker'}`
          : `Disable ${space ? 'space' : 'locker'}`,
      Icon: status !== KnownDeliveryContainerDtoStatus.Active ? ActionEnableLocker : ActionDisableLocker,
      action: LockerDetailsActions.ENABLE_DISABLE_LOCKER,
      payload: {
        deliveryContainerID
      },
      permission: {
        action: Permissions.deliveryContainers.update,
        projectID
      }
    },
    {
      title: 'Unlock room',
      Icon: ActionOpen,
      action: LockerDetailsActions.UNLOCK_ROOM,
      payload: {
        accessControl: section?.accessControl
      },
      permission: {
        action: Permissions.deliveryContainers.open,
        projectID
      }
    },
    {
      title: 'Edit capabilities',
      Icon: Edit,
      action: LockerDetailsActions.EDIT_PROJECT_MODULES,
      payload: {
        deliveryContainerID,
        projectIDs: hub.acceptsDeliveriesForProjectIDs
      },
      permission: {
        action: Permissions.deliveryContainers.update,
        projectID
      },
      availableUiFeatures: ['experimental']
    },
    {
      title: `Reserve ${space ? 'space' : 'locker'}`,
      Icon: Reserve,
      action: LockerDetailsActions.RESERVE_CONTAINER,
      payload: {
        deliveryContainerID
      },
      permission: {
        action: Permissions.deliveryContainers.update,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    },
    {
      title: `Unreserve ${space ? 'space' : 'locker'}`,
      Icon: Unreserve,
      action: LockerDetailsActions.UNRESERVE_CONTAINER,
      payload: {
        deliveryContainerID
      },
      permission: {
        action: Permissions.deliveryContainers.update,
        projectID
      },
      availableUiFeatures: ['internal-groundfloor']
    }
  ];

  return actions;
}

function makeValidateAction(
  deliveryContainerID: string,
  hubDetails: HubDetails,
  hasInternalUIFeatures: boolean,
  isLockerDetailsPage: boolean
) {
  const section = hubDetails.sections?.find((s) => s.spaces?.some((sp) => sp.id === deliveryContainerID));
  const tower = hubDetails.towers?.find((t) => t.lockers?.some((l) => l.id === deliveryContainerID));
  const space = section?.spaces?.find((s) => s.id === deliveryContainerID);
  const locker = tower?.lockers?.find((l) => l.id === deliveryContainerID);

  const isKiosk = space?.isKiosk || locker?.isKiosk;
  const isLocker = !!locker;
  // Reserved-for-a-party state is the presence of reservedForRecipients — this is unrelated
  // to the `availability` enum, which tracks delivery occupancy (see DeliveryContainer model).
  const reservedForPartyCount = (space ?? locker)?.reservedForRecipients?.length ?? 0;

  const isExternallyControlled = hubDetails.kiosk?.controlledBy === KnownHubDetailsKioskDtoControlledBy.ExternalAc;

  // Each action is validated separately.
  // This is to ensure any actions added in the future are not automatically blocked by the internal UI features flag.
  return function validateAction(action: LockerDetailsActions) {
    const isExternalLockersHub = hubDetails.type === KnownHubDetailsDtoType.LockersHubExternal;

    if (action === LockerDetailsActions.OPEN_LOCKER) {
      return isLocker && !isExternalLockersHub && (!isKiosk || hasInternalUIFeatures);
    }
    if (action === LockerDetailsActions.ENABLE_DISABLE_LOCKER) {
      return (isLocker || !!space) && (!isKiosk || hasInternalUIFeatures);
    }
    if (action === LockerDetailsActions.UNLOCK_ROOM) {
      return !isExternalLockersHub &&
        section &&
        section.accessControl &&
        section.accessControl.type !== KnownAccessControlDtoType.Code
        ? true
        : false;
    }
    if (action === LockerDetailsActions.EDIT_PROJECT_MODULES) {
      return !isExternallyControlled && !isExternalLockersHub && hasInternalUIFeatures && !isLockerDetailsPage;
    }
    // Show "Reserve" only when not already reserved for a party, and "Unreserve" only when it is.
    if (action === LockerDetailsActions.RESERVE_CONTAINER) {
      return (isLocker || !!space) && !isKiosk && reservedForPartyCount === 0;
    }
    if (action === LockerDetailsActions.UNRESERVE_CONTAINER) {
      return (isLocker || !!space) && !isKiosk && reservedForPartyCount > 0;
    }
    return true;
  };
}

export function makeDefineLockerDetailsActions(
  projectID: string,
  deliveryContainerID: string,
  hubDetails: HubDetails,
  hub: HubDto,
  hasInternalUIFeatures: boolean,
  isLockerDetailsPage: boolean
) {
  return makeDefineActions(
    makeLockerActions(projectID, deliveryContainerID, hubDetails, hub),
    undefined,
    makeValidateAction(deliveryContainerID, hubDetails, hasInternalUIFeatures, isLockerDetailsPage)
  );
}
